const DEFAULT_PRODUCT_DESTINATION = '/sales-choose';
const POST_SIGNUP_JOURNEY_KEY = 'botspot_post_signup_journey_v1';
const PENDING_POST_SIGNUP_DESTINATION_KEY = 'botspot_pending_post_signup_destination_v1';
const POST_SIGNUP_ROUTING_GUARD_KEY = 'botspot_post_signup_routing_guard_v1';
const POST_SIGNUP_JOURNEY_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const POST_SIGNUP_ROUTING_GUARD_MS = 30 * 1000;

type PostSignupJourney = {
  userId: string;
  source: string;
  startedAt: number;
  destination?: string;
};

export const POST_SIGNUP_CHECKOUT_EXPERIMENT = {
  name: 'post_signup_checkout_gate_v1',
  featureFlag: 'post_signup_checkout_gate_v1_variant',
} as const;

export function sanitizeInternalDestination(
  value: string | null | undefined,
  fallback = DEFAULT_PRODUCT_DESTINATION,
): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return fallback;
  }
  try {
    const parsed = new URL(value, window.location.origin);
    if (parsed.origin !== window.location.origin) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function buildPostSignupDecisionPath(
  destination: string,
  forcedCheckoutVariant?: string | null,
): string {
  const safeDestination = sanitizeInternalDestination(destination);
  const params = new URLSearchParams({
    redirectTo: safeDestination,
  });
  const destinationParams = new URL(safeDestination, window.location.origin).searchParams;
  const forcedVariant = forcedCheckoutVariant || destinationParams.get('postSignupCheckoutVariant');
  if (forcedVariant === 'control' || forcedVariant === 'test') {
    params.set('postSignupCheckoutVariant', forcedVariant);
  }
  return `/post-signup?${params.toString()}`;
}

export function getJwtUserId(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return null;
    const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(padded));
    const userId = payload?.userId;
    return typeof userId === 'string' || typeof userId === 'number'
      ? String(userId)
      : null;
  } catch {
    return null;
  }
}

export function startPostSignupJourney(
  userId: unknown,
  source: string,
  destination?: string | null,
): boolean {
  if (typeof window === 'undefined') return false;
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return false;
  const journey: PostSignupJourney = {
    userId: normalizedUserId,
    source,
    startedAt: Date.now(),
    ...(destination ? { destination: sanitizeInternalDestination(destination) } : {}),
  };
  try {
    window.sessionStorage.removeItem(POST_SIGNUP_ROUTING_GUARD_KEY);
    window.sessionStorage.setItem(POST_SIGNUP_JOURNEY_KEY, JSON.stringify(journey));
    return true;
  } catch {
    return false;
  }
}

export function rememberPendingPostSignupDestination(destination: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      PENDING_POST_SIGNUP_DESTINATION_KEY,
      sanitizeInternalDestination(destination),
    );
  } catch {
    // Session storage can be unavailable in privacy-restricted browsers.
  }
}

export function takePendingPostSignupDestination(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const destination = window.sessionStorage.getItem(PENDING_POST_SIGNUP_DESTINATION_KEY);
    window.sessionStorage.removeItem(PENDING_POST_SIGNUP_DESTINATION_KEY);
    return destination ? sanitizeInternalDestination(destination) : null;
  } catch {
    return null;
  }
}

export function hasActivePostSignupJourney(userId: unknown): boolean {
  if (typeof window === 'undefined') return false;
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return false;
  try {
    const stored = window.sessionStorage.getItem(POST_SIGNUP_JOURNEY_KEY);
    if (!stored) return false;
    const journey = JSON.parse(stored) as Partial<PostSignupJourney>;
    const isActive = journey.userId === normalizedUserId
      && typeof journey.startedAt === 'number'
      && Date.now() - journey.startedAt <= POST_SIGNUP_JOURNEY_MAX_AGE_MS;
    if (!isActive) {
      window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
    }
    return isActive;
  } catch {
    try {
      window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
    } catch {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
    return false;
  }
}

export function hasPendingPostSignupJourney(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.sessionStorage.getItem(POST_SIGNUP_JOURNEY_KEY);
    if (stored) {
      const journey = JSON.parse(stored) as Partial<PostSignupJourney>;
      const isPending = Boolean(String(journey.userId || '').trim())
        && typeof journey.startedAt === 'number'
        && Date.now() - journey.startedAt <= POST_SIGNUP_JOURNEY_MAX_AGE_MS;
      if (isPending) return true;
      window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
    }

    const guardExpiresAt = Number(
      window.sessionStorage.getItem(POST_SIGNUP_ROUTING_GUARD_KEY) || 0,
    );
    if (Number.isFinite(guardExpiresAt) && guardExpiresAt > Date.now()) {
      return true;
    }
    window.sessionStorage.removeItem(POST_SIGNUP_ROUTING_GUARD_KEY);
    return false;
  } catch {
    try {
      window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
    } catch {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
    return false;
  }
}

export function getActivePostSignupDestination(
  userId: unknown,
  fallback: string,
): string {
  if (typeof window === 'undefined') return sanitizeInternalDestination(fallback);
  const normalizedUserId = String(userId || '').trim();
  try {
    const stored = window.sessionStorage.getItem(POST_SIGNUP_JOURNEY_KEY);
    if (!stored) return sanitizeInternalDestination(fallback);
    const journey = JSON.parse(stored) as Partial<PostSignupJourney>;
    if (
      journey.userId !== normalizedUserId ||
      typeof journey.startedAt !== 'number' ||
      Date.now() - journey.startedAt > POST_SIGNUP_JOURNEY_MAX_AGE_MS
    ) {
      window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
      return sanitizeInternalDestination(fallback);
    }
    return sanitizeInternalDestination(journey.destination, fallback);
  } catch {
    try {
      window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
    } catch {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
    return sanitizeInternalDestination(fallback);
  }
}

export function completePostSignupJourney(
  options: { preserveRoutingGuard?: boolean } = {},
): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(POST_SIGNUP_JOURNEY_KEY);
    if (options.preserveRoutingGuard) {
      window.sessionStorage.setItem(
        POST_SIGNUP_ROUTING_GUARD_KEY,
        String(Date.now() + POST_SIGNUP_ROUTING_GUARD_MS),
      );
    } else {
      window.sessionStorage.removeItem(POST_SIGNUP_ROUTING_GUARD_KEY);
    }
  } catch {
    // Session storage can be unavailable in privacy-restricted browsers.
  }
}

export function buildImmediateCheckoutPath(destination: string): string {
  const params = new URLSearchParams({
    redirect: 'starter_plan',
    postSignupDestination: sanitizeInternalDestination(destination),
    post_signup_checkout_gate_v1_variant: 'test',
  });
  return `/checkout/starter-plan?${params.toString()}`;
}

export function buildPostPurchaseChallengePath(
  destination: string,
  sourceFlow: string,
): string {
  const params = new URLSearchParams({
    post_purchase: 'true',
    source_flow: sourceFlow,
    redirectTo: sanitizeInternalDestination(
      destination,
      '/account-settings/subscriptions',
    ),
  });
  return `/challenges?${params.toString()}`;
}

export function isStarterOrLightProduct(productSlug: unknown): boolean {
  return productSlug === 'starter-plan' || productSlug === 'light-plan';
}
